/**
 * PipelineFlow Component
 *
 * Main container for the pipeline visualization showing the product lifecycle
 * from import to measurement with prerequisites, metrics, and flow percentages.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, Workflow } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { PipelineStepBox, PipelineStep } from './PipelineStepBox';
import { PipelineConnector } from './PipelineConnector';
import { PipelinePrerequisites } from './PipelinePrerequisites';
import { PipelineMetrics } from './PipelineMetrics';

interface PipelineFlowProps {
  siteId: string;
  onRefresh?: () => void;
}

interface PipelineData {
  siteId: string;
  steps: PipelineStep[];
  analyzedAt: string;
}

export function PipelineFlow({ siteId, onRefresh }: PipelineFlowProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PipelineData | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const response = await authFetch(`/api/aeo-readiness/site/${siteId}/pipeline`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load pipeline data');
      }

      const result = await response.json();
      setData(result.data);
    } catch (err) {
      console.error('Failed to load pipeline flow:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadData();
    if (onRefresh) onRefresh();
  };

  const handleStepAction = async (step: PipelineStep) => {
    if (step.action.inlineAction === 'generateEmbeddings') {
      setActionLoading(step.id);
      try {
        const response = await authFetch(`/api/aeo-readiness/site/${siteId}/action/generate-embeddings`, {
          method: 'POST'
        });
        if (response.ok) {
          handleRefresh();
        }
      } catch (err) {
        console.error('Failed to trigger embeddings:', err);
      } finally {
        setActionLoading(null);
      }
    } else if (step.action.url) {
      navigate(step.action.url);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 overflow-x-auto pb-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-32 w-36" />
                {i < 7 && <Skeleton className="h-5 w-10" />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={handleRefresh} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Workflow className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Product Pipeline Flow</CardTitle>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <CardDescription>
          Track products through the AEO content lifecycle from import to measurement
        </CardDescription>
      </CardHeader>

      <CardContent>
        {/* Pipeline Flow Visualization */}
        <div className="overflow-x-auto pb-4 -mx-2 px-2">
          <div className="flex items-start min-w-max">
            {data.steps.map((step, index) => (
              <div key={step.id} className="flex items-start">
                {/* Step Column */}
                <div className="flex flex-col items-center">
                  {/* Prerequisites (above step) */}
                  <PipelinePrerequisites prerequisites={step.prerequisites} />

                  {/* Step Box */}
                  <PipelineStepBox
                    step={step}
                    onAction={handleStepAction}
                    isActionLoading={actionLoading === step.id}
                  />

                  {/* Metrics (below step) */}
                  <PipelineMetrics
                    count={step.count}
                    percentageFromPrevious={step.percentageFromPrevious}
                    lastActivity={step.metrics.lastActivity}
                    qualityIndicator={step.metrics.qualityIndicator}
                  />
                </div>

                {/* Connector (between steps) */}
                {index < data.steps.length - 1 && (
                  <div className="flex items-center self-center mt-8">
                    <PipelineConnector
                      percentage={data.steps[index + 1].percentageFromPrevious}
                      status={data.steps[index + 1].status}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Complete (&gt;80%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span>In Progress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span>Needs Attention</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-300" />
            <span>Blocked</span>
          </div>
        </div>

        {/* Last Updated */}
        <p className="text-xs text-muted-foreground text-center mt-2">
          Last analyzed: {new Date(data.analyzedAt).toLocaleString()}
        </p>
      </CardContent>
    </Card>
  );
}
